import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { Eye } from '@styled-icons/fa-solid/Eye';
import { EyeSlash } from '@styled-icons/fa-solid/EyeSlash';
import { Key } from '@styled-icons/fa-solid/Key';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { API_URL } from '../../constants/string-constants';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle,
  ModernButton,
  ModernButtonSecondary,
} from './shared/ModalBase';
import {
  FieldSection,
  FieldLabel,
  FieldInput,
  FieldSelect,
  InlineFieldContainer,
  PrivacyToggle,
  CharacterCount,
  ErrorMessage,
  ButtonContainer,
} from './shared/FormComponents';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  color: var(--clr-sec);
  opacity: 0.7;
  z-index: 10;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
    transform: scale(1.1);
  }
`;

const AccountNumberSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(var(--clr-pri-rgb), 0.1);
  border-radius: 8px;
  border: 1px solid rgba(var(--clr-sec-rgb), 0.2);
`;

const AccountNumberButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--clr-sec);
  color: var(--clr-sec);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: rgba(var(--clr-sec-rgb), 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AccountNumberDisplay = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  font-family: monospace;
  font-size: 1rem;
  letter-spacing: 0.1em;
  color: var(--clr-sec);
  word-break: break-all;
  user-select: all;
`;

const AccountNumberNote = styled.p`
  font-size: 0.75rem;
  color: var(--clr-sec);
  opacity: 0.7;
  margin-top: 0.5rem;
  margin-bottom: 0;
`;

const EditProfileModal = ({
  visible,
  onClose,
  onProfileUpdate,
  currentProfile = {},
}) => {
  const [profileData, setProfileData] = useState({
    nickname: currentProfile.nickname || '',
    birthday: currentProfile.birthday || '',
    pronouns: currentProfile.pronouns || '',
    bio: currentProfile.bio || '',
    agePublic: currentProfile.agePublic !== false,
    pronounsPublic: currentProfile.pronounsPublic !== false,
    preferredStatus: currentProfile.preferredStatus || '',
  });

  const [serverBirthday, setServerBirthday] = useState(null);
  const [showCustomPronouns, setShowCustomPronouns] = useState(false);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [hasAccountNumber, setHasAccountNumber] = useState(false);
  const [accountNumber, setAccountNumber] = useState(null);
  const [isLoadingAccountNumber, setIsLoadingAccountNumber] = useState(false);

  useEffect(() => {
    // Load current profile data when modal opens
    if (visible) {
      loadCurrentProfile();
      // Reset account number display state when modal opens
      setAccountNumber(null);
    }
  }, [visible]);

  const calculateAge = (birthday) => {
    if (!birthday) return null;
    const today = new Date();
    const birthDate = new Date(birthday);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const loadCurrentProfile = async () => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;

      const token = await auth.currentUser.getIdToken();

      // Fetch profile and status in parallel
      const [profileResponse, statusResponse] = await Promise.all([
        fetch(API_URL + 'users/profile', {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }),
        fetch(API_URL + 'users/status', {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }),
      ]);

      if (profileResponse.ok) {
        const data = await profileResponse.json();
        setServerBirthday(data.birthday); // Store server birthday state
        const pronouns = data.pronouns || '';
        const isCustomPronouns = pronouns && !pronounOptions.find(opt => opt.value === pronouns && opt.value !== 'custom');

        setProfileData({
          nickname: data.nickname || '',
          birthday: data.birthday || '',
          pronouns: pronouns,
          bio: data.bio || '',
          agePublic: data.agePublic !== false,
          pronounsPublic: data.pronounsPublic !== false,
          preferredStatus: data.preferredStatus || '',
        });
        setShowCustomPronouns(isCustomPronouns);
      }

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setHasAccountNumber(statusData.hasAccountNumber);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const fetchAccountNumber = async () => {
    setIsLoadingAccountNumber(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'account/retrieve', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccountNumber(data.account_number);
      } else {
        toast.error('Failed to retrieve account number');
      }
    } catch (err) {
      console.error('Error fetching account number:', err);
      toast.error('Failed to retrieve account number');
    } finally {
      setIsLoadingAccountNumber(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Nickname validation
    if (!profileData.nickname || profileData.nickname.trim() === '') {
      newErrors.nickname = 'Nickname is required';
    } else if (profileData.nickname.length > 20) {
      newErrors.nickname = 'Nickname cannot exceed 20 characters';
    }

    // Birthday validation - only validate if they're setting a new birthday
    if (profileData.birthday && !serverBirthday) {
      const birthDate = new Date(profileData.birthday);
      const today = new Date();

      if (isNaN(birthDate.getTime())) {
        newErrors.birthday = 'Please enter a valid date';
      } else if (birthDate > today) {
        newErrors.birthday = 'Birthday cannot be in the future';
      } else {
        const age = calculateAge(profileData.birthday);
        if (age < 16) {
          newErrors.birthday =
            "You must be 16 or older to use VentScape.";
        } else if (age > 120) {
          newErrors.birthday = 'Please enter a valid birth date';
        }
      }
    }

    // Pronouns validation
    if (profileData.pronouns && profileData.pronouns.length > 20) {
      newErrors.pronouns = 'Pronouns cannot exceed 20 characters';
    }
  
    // Bio validation
    if (profileData.bio && profileData.bio.length > 1000) {
      newErrors.bio = 'Bio cannot exceed 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to update your profile');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      // Don't send birthday if it's already set on the server
      const dataToSend = { ...profileData };
      if (serverBirthday) {
        delete dataToSend.birthday;
      }

      const response = await fetch(API_URL + 'users/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Profile updated successfully!');
        onProfileUpdate(profileData);
        onClose();
      } else {
        setErrors({ general: result.message || 'Failed to update profile' });
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const togglePrivacy = (field) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const pronounOptions = [
    { value: '', label: 'Select pronouns (optional)' },
    { value: 'she/her', label: 'she/her' },
    { value: 'he/him', label: 'he/him' },
    { value: 'they/them', label: 'they/them' },
    { value: 'she/they', label: 'she/they' },
    { value: 'he/they', label: 'he/they' },
    { value: 'they/she', label: 'they/she' },
    { value: 'they/he', label: 'they/he' },
    { value: 'it/its', label: 'it/its' },
    { value: 'custom', label: 'Custom...' },
  ];

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />
          <ModernTitle>Edit Profile</ModernTitle>
          <ModernSubtitle>
            Customize how you appear to others on VentScape
          </ModernSubtitle>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

          <FieldSection>
            <FieldLabel>Nickname *</FieldLabel>
            <FieldInput
              type='text'
              placeholder='Enter your nickname'
              value={profileData.nickname}
              onChange={(e) => handleInputChange('nickname', e.target.value)}
              className={errors.nickname ? 'error' : ''}
              maxLength={20}
            />
            <CharacterCount>{profileData.nickname.length}/20</CharacterCount>
            <p style={{ color: 'var(--clr-sec)', fontSize: '0.75rem', margin: '0.25rem 0 0 0', opacity: 0.6 }}>
              You can only change your nickname once per week
            </p>
            {errors.nickname && <ErrorMessage>{errors.nickname}</ErrorMessage>}

            <FieldLabel style={{ marginTop: '1.5rem' }}>
              Birthday
              {!serverBirthday && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    opacity: 0.7,
                    fontWeight: 'normal',
                    marginLeft: '0.5rem',
                  }}
                >
                  (Please use your real age, can only be set once)
                </span>
              )}
            </FieldLabel>
            <InlineFieldContainer>
              <FieldInput
                type='date'
                placeholder='Select your birthday'
                value={profileData.birthday}
                onChange={(e) => handleInputChange('birthday', e.target.value)}
                className={`${errors.birthday ? 'error' : ''} compact`}
                disabled={!!serverBirthday}
              />
              <PrivacyToggle
                isPublic={profileData.agePublic}
                onClick={() => togglePrivacy('agePublic')}
                type='button'
              >
                {profileData.agePublic ? <Eye /> : <EyeSlash />}
                {profileData.agePublic ? 'Public' : 'Private'}
              </PrivacyToggle>
            </InlineFieldContainer>
            {profileData.birthday && (
              <p
                style={{
                  fontSize: '0.8rem',
                  color: 'var(--clr-sec)',
                  opacity: 0.7,
                  marginTop: '0.5rem',
                }}
              >
                Age: {calculateAge(profileData.birthday)} years old
                {serverBirthday && ' (Birthday cannot be changed once set)'}
              </p>
            )}
            {errors.birthday && <ErrorMessage>{errors.birthday}</ErrorMessage>}

            <FieldLabel style={{ marginTop: '1.5rem' }}>Pronouns</FieldLabel>
            <InlineFieldContainer>
              <FieldSelect
                className='compact'
                value={
                  showCustomPronouns ? 'custom' : profileData.pronouns
                }
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setShowCustomPronouns(true);
                    handleInputChange('pronouns', '');
                  } else {
                    setShowCustomPronouns(false);
                    handleInputChange('pronouns', e.target.value);
                  }
                }}
              >
                {pronounOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FieldSelect>
              <PrivacyToggle
                isPublic={profileData.pronounsPublic}
                onClick={() => togglePrivacy('pronounsPublic')}
                type='button'
              >
                {profileData.pronounsPublic ? <Eye /> : <EyeSlash />}
                {profileData.pronounsPublic ? 'Public' : 'Private'}
              </PrivacyToggle>
            </InlineFieldContainer>

            {showCustomPronouns && (
              <>
                <FieldInput
                  type='text'
                  placeholder='Enter custom pronouns (e.g., ze/zir, fae/faer)'
                  value={profileData.pronouns}
                  onChange={(e) =>
                    handleInputChange('pronouns', e.target.value)
                  }
                  className={errors.pronouns ? 'error' : ''}
                  maxLength={20}
                  style={{ marginTop: '0.5rem' }}
                />
                <CharacterCount>
                  {profileData.pronouns.length}/20
                </CharacterCount>
              </>
            )}
            {errors.pronouns && <ErrorMessage>{errors.pronouns}</ErrorMessage>}

            <FieldLabel style={{ marginTop: '1.5rem' }}>Bio</FieldLabel>
            <FieldInput
              as='textarea'
              placeholder='Tell others about yourself - your interests, hobbies, what brings you to VentScape (optional)'
              value={profileData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className={errors.bio ? 'error' : ''}
              maxLength={1000}
              rows={4}
              style={{ resize: 'vertical', minHeight: '100px' }}
            />
            <CharacterCount>{profileData.bio.length}/1000</CharacterCount>
            {errors.bio && <ErrorMessage>{errors.bio}</ErrorMessage>}

            <FieldLabel style={{ marginTop: '1.5rem' }}>Status</FieldLabel>
            <FieldSelect
              value={profileData.preferredStatus}
              onChange={(e) => handleInputChange('preferredStatus', e.target.value)}
            >
              <option value="">🟢 Default (Show online when active)</option>
              <option value="offline">⚫ Appear Offline</option>
            </FieldSelect>
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--clr-sec)',
                opacity: 0.7,
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span 
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: profileData.preferredStatus === 'offline' ? '#6c757d' : '#28a745',
                  display: 'inline-block',
                }}
              />
              {profileData.preferredStatus === 'offline'
                ? 'You will appear offline to other users, even when active'
                : 'Your online status will be visible to friends based on your activity'
              }
            </p>

            {hasAccountNumber && (
              <AccountNumberSection>
                <FieldLabel style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                  Account Number
                </FieldLabel>
                {!accountNumber ? (
                  <AccountNumberButton
                    onClick={fetchAccountNumber}
                    disabled={isLoadingAccountNumber}
                    type='button'
                  >
                    <Key />
                    {isLoadingAccountNumber ? 'Loading...' : 'Show Account Number'}
                  </AccountNumberButton>
                ) : (
                  <>
                    <AccountNumberDisplay>
                      {accountNumber}
                    </AccountNumberDisplay>
                    <AccountNumberNote>
                      Keep this number private. You can use it to sign in on other devices.
                    </AccountNumberNote>
                  </>
                )}
              </AccountNumberSection>
            )}
          </FieldSection>

          <ButtonContainer>
            <ModernButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Profile ✨'}
            </ModernButton>
            <ModernButtonSecondary onClick={onClose} type='button'>
              Cancel
            </ModernButtonSecondary>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default EditProfileModal;
